import { createFileRoute } from "@tanstack/react-router";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import { useMemo, useState } from "react";
import { useScope } from "@/hooks/use-scope";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import {
  Card, CardContent, CardDescription, CardHeader, CardTitle,
} from "@/components/ui/card";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { toast } from "sonner";
import { Bell, CheckCircle2, Loader2, Plus, Trash2, XCircle } from "lucide-react";
import { formatDistanceToNow } from "date-fns";
import {
  NOTIFICATION_CHANNELS,
  NOTIFICATION_EVENTS,
  type NotificationChannel,
  deleteNotificationSubscription,
  listNotificationDeliveries,
  listNotificationSubscriptions,
  toggleNotificationSubscription,
  upsertNotificationSubscription,
} from "@/lib/notifications-admin.functions";

export const Route = createFileRoute("/_authenticated/app/settings/notifications")({
  head: () => ({
    meta: [
      { title: "Notification preferences — CairnOS" },
      {
        name: "description",
        content: "Route operational events to email, Slack, or webhooks and audit recent deliveries.",
      },
    ],
  }),
  component: NotificationsPage,
});

const CATEGORIES = Array.from(new Set(NOTIFICATION_EVENTS.map((e) => e.category)));

function targetPlaceholder(ch: NotificationChannel) {
  if (ch === "email") return "ops@yourcompany.com";
  if (ch === "slack") return "https://hooks.slack.com/services/...";
  return "https://api.yourcompany.com/hooks/cairnos";
}

function validateTarget(ch: NotificationChannel, t: string): string | null {
  if (ch === "email") {
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(t)) return "Enter a valid email address.";
    return null;
  }
  try {
    const u = new URL(t);
    if (u.protocol !== "https:") return "URL must use https://";
    if (ch === "slack" && !u.hostname.endsWith("slack.com")) return "Slack webhooks should point to slack.com.";
    return null;
  } catch {
    return "Enter a valid URL.";
  }
}

function NotificationsPage() {
  const { tenantId } = useScope();
  const qc = useQueryClient();

  const listFn = useServerFn(listNotificationSubscriptions);
  const delivFn = useServerFn(listNotificationDeliveries);
  const upsertFn = useServerFn(upsertNotificationSubscription);
  const delFn = useServerFn(deleteNotificationSubscription);
  const toggleFn = useServerFn(toggleNotificationSubscription);

  const subs = useQuery({
    queryKey: ["notif-subs", tenantId],
    enabled: !!tenantId,
    queryFn: () => listFn({ data: { tenantId: tenantId! } }),
  });

  const deliveries = useQuery({
    queryKey: ["notif-deliveries", tenantId],
    enabled: !!tenantId,
    queryFn: () => delivFn({ data: { tenantId: tenantId!, limit: 100 } }),
  });

  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState<any | null>(null);
  const [eventType, setEventType] = useState(NOTIFICATION_EVENTS[0].id);
  const [channel, setChannel] = useState<NotificationChannel>("email");
  const [target, setTarget] = useState("");
  const [active, setActive] = useState(true);

  const openNew = () => {
    setEditing(null);
    setEventType(NOTIFICATION_EVENTS[0].id);
    setChannel("email");
    setTarget("");
    setActive(true);
    setOpen(true);
  };
  const openEdit = (row: any) => {
    setEditing(row);
    setEventType(row.event_type);
    setChannel(row.channel);
    setTarget(row.target);
    setActive(row.active);
    setOpen(true);
  };

  const save = useMutation({
    mutationFn: async () => {
      const err = validateTarget(channel, target);
      if (err) throw new Error(err);
      return upsertFn({
        data: {
          id: editing?.id,
          tenantId: tenantId!,
          eventType,
          channel,
          target: target.trim(),
          active,
        },
      });
    },
    onSuccess: () => {
      toast.success(editing ? "Subscription updated" : "Subscription added");
      setOpen(false);
      qc.invalidateQueries({ queryKey: ["notif-subs", tenantId] });
    },
    onError: (e: any) => toast.error(e?.message ?? "Failed to save"),
  });

  const remove = useMutation({
    mutationFn: (id: string) => delFn({ data: { tenantId: tenantId!, id } }),
    onSuccess: () => {
      toast.success("Subscription removed");
      qc.invalidateQueries({ queryKey: ["notif-subs", tenantId] });
    },
    onError: (e: any) => toast.error(e?.message ?? "Failed to remove"),
  });

  const toggle = useMutation({
    mutationFn: (v: { id: string; active: boolean }) =>
      toggleFn({ data: { tenantId: tenantId!, ...v } }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["notif-subs", tenantId] }),
    onError: (e: any) => toast.error(e?.message ?? "Failed to toggle"),
  });

  const rows = subs.data?.rows ?? [];
  const delivRows = deliveries.data?.rows ?? [];

  const stats = useMemo(() => {
    const total = rows.length;
    const activeCount = rows.filter((r: any) => r.active).length;
    const byChannel = NOTIFICATION_CHANNELS.reduce<Record<string, number>>(
      (acc, c) => ({ ...acc, [c]: rows.filter((r: any) => r.channel === c).length }),
      {},
    );
    const failures = delivRows.filter((d: any) => d.error || (d.http_status && d.http_status >= 400)).length;
    return { total, activeCount, byChannel, failures };
  }, [rows, delivRows]);

  const eventLabel = (id: string) =>
    NOTIFICATION_EVENTS.find((e) => e.id === id)?.label ?? id;

  return (
    <div className="container mx-auto p-6 max-w-6xl space-y-6">
      <header className="flex items-start justify-between gap-4">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight flex items-center gap-2">
            <Bell className="h-6 w-6" /> Notification preferences
          </h1>
          <p className="text-sm text-muted-foreground mt-1">
            Route operational events to the right people. Wire up email, Slack, or webhooks and audit
            every delivery in one place.
          </p>
        </div>
        <Button onClick={openNew}>
          <Plus className="h-4 w-4 mr-2" /> New subscription
        </Button>
      </header>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <StatCard label="Subscriptions" value={stats.total} />
        <StatCard label="Active" value={stats.activeCount} />
        <StatCard label="Channels in use" value={Object.values(stats.byChannel).filter((v) => v > 0).length} />
        <StatCard label="Recent failures" value={stats.failures} tone={stats.failures > 0 ? "warn" : "default"} />
      </div>

      <Tabs defaultValue="rules">
        <TabsList>
          <TabsTrigger value="rules">Routing rules</TabsTrigger>
          <TabsTrigger value="history">Delivery history</TabsTrigger>
        </TabsList>

        <TabsContent value="rules" className="mt-4">
          <Card>
            <CardHeader>
              <CardTitle className="text-base">Subscriptions</CardTitle>
              <CardDescription>
                Each rule fires when its event type is emitted. Toggle without deleting to mute temporarily.
              </CardDescription>
            </CardHeader>
            <CardContent>
              {subs.isLoading ? (
                <div className="flex items-center justify-center py-10 text-muted-foreground">
                  <Loader2 className="h-4 w-4 animate-spin mr-2" /> Loading…
                </div>
              ) : rows.length === 0 ? (
                <div className="text-center py-12 text-sm text-muted-foreground">
                  No subscriptions yet. Add one to start routing events.
                </div>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Event</TableHead>
                      <TableHead>Channel</TableHead>
                      <TableHead>Target</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead className="text-right">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {rows.map((r: any) => (
                      <TableRow key={r.id}>
                        <TableCell>
                          <div className="font-medium">{eventLabel(r.event_type)}</div>
                          <div className="text-xs text-muted-foreground font-mono">{r.event_type}</div>
                        </TableCell>
                        <TableCell>
                          <Badge variant="outline" className="capitalize">{r.channel}</Badge>
                        </TableCell>
                        <TableCell className="max-w-[280px] truncate font-mono text-xs">{r.target}</TableCell>
                        <TableCell>
                          <div className="flex items-center gap-2">
                            <Switch
                              checked={r.active}
                              onCheckedChange={(v) => toggle.mutate({ id: r.id, active: v })}
                            />
                            <span className="text-xs text-muted-foreground">
                              {r.active ? "Active" : "Muted"}
                            </span>
                          </div>
                        </TableCell>
                        <TableCell className="text-right space-x-2">
                          <Button variant="ghost" size="sm" onClick={() => openEdit(r)}>Edit</Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => {
                              if (confirm("Remove this subscription?")) remove.mutate(r.id);
                            }}
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="history" className="mt-4">
          <Card>
            <CardHeader>
              <CardTitle className="text-base">Recent Deliveries</CardTitle>
              <CardDescription>
                Last 100 notification dispatches across all channels. Use this to diagnose silent rules.
              </CardDescription>
            </CardHeader>
            <CardContent>
              {deliveries.isLoading ? (
                <div className="flex items-center justify-center py-10 text-muted-foreground">
                  <Loader2 className="h-4 w-4 animate-spin mr-2" /> Loading…
                </div>
              ) : delivRows.length === 0 ? (
                <div className="text-center py-12 text-sm text-muted-foreground">
                  No deliveries recorded yet.
                </div>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>When</TableHead>
                      <TableHead>Event</TableHead>
                      <TableHead>Channel</TableHead>
                      <TableHead>Target</TableHead>
                      <TableHead>Result</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {delivRows.map((d: any) => {
                      const ok = !d.error && (!d.http_status || d.http_status < 400);
                      return (
                        <TableRow key={d.id}>
                          <TableCell className="text-xs text-muted-foreground whitespace-nowrap">
                            {formatDistanceToNow(new Date(d.sent_at), { addSuffix: true })}
                          </TableCell>
                          <TableCell className="font-mono text-xs">{d.event_type}</TableCell>
                          <TableCell>
                            <Badge variant="outline" className="capitalize">{d.channel}</Badge>
                          </TableCell>
                          <TableCell className="max-w-[260px] truncate font-mono text-xs">{d.target}</TableCell>
                          <TableCell>
                            {ok ? (
                              <span className="inline-flex items-center gap-1 text-xs text-emerald-600 dark:text-emerald-400">
                                <CheckCircle2 className="h-3.5 w-3.5" />
                                {d.http_status ?? "OK"}
                              </span>
                            ) : (
                              <span className="inline-flex items-center gap-1 text-xs text-destructive">
                                <XCircle className="h-3.5 w-3.5" />
                                {d.http_status ?? "ERR"}
                                {d.error ? ` — ${String(d.error).slice(0, 60)}` : ""}
                              </span>
                            )}
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>{editing ? "Edit subscription" : "New subscription"}</DialogTitle>
            <DialogDescription>
              Pick an event, a delivery channel, and where to send it.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            <div className="space-y-2">
              <Label>Event</Label>
              <Select value={eventType} onValueChange={setEventType}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  {CATEGORIES.map((cat) => (
                    <div key={cat}>
                      <div className="px-2 py-1 text-[10px] uppercase tracking-wider text-muted-foreground">
                        {cat}
                      </div>
                      {NOTIFICATION_EVENTS.filter((e) => e.category === cat).map((e) => (
                        <SelectItem key={e.id} value={e.id}>
                          <div className="flex flex-col">
                            <span>{e.label}</span>
                            <span className="text-xs text-muted-foreground">{e.description}</span>
                          </div>
                        </SelectItem>
                      ))}
                    </div>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label>Channel</Label>
              <Select value={channel} onValueChange={(v) => setChannel(v as NotificationChannel)}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  {NOTIFICATION_CHANNELS.map((c) => (
                    <SelectItem key={c} value={c} className="capitalize">{c}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label>Target</Label>
              <Input
                value={target}
                onChange={(e) => setTarget(e.target.value)}
                placeholder={targetPlaceholder(channel)}
              />
              <p className="text-xs text-muted-foreground">
                {channel === "email"
                  ? "Comma-separated lists are not supported — add one subscription per address."
                  : "Must be an https:// URL. Slack: incoming-webhook URL."}
              </p>
            </div>

            <div className="flex items-center justify-between rounded-md border p-3">
              <div>
                <div className="text-sm font-medium">Active</div>
                <div className="text-xs text-muted-foreground">Disable to mute without deleting.</div>
              </div>
              <Switch checked={active} onCheckedChange={setActive} />
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
            <Button onClick={() => save.mutate()} disabled={save.isPending}>
              {save.isPending && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
              {editing ? "Save changes" : "Create subscription"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function StatCard({
  label,
  value,
  tone = "default",
}: {
  label: string;
  value: number | string;
  tone?: "default" | "warn";
}) {
  return (
    <Card>
      <CardContent className="pt-6">
        <div className="text-xs uppercase tracking-wider text-muted-foreground">{label}</div>
        <div
          className={
            "text-2xl font-semibold mt-1 " +
            (tone === "warn" && Number(value) > 0 ? "text-destructive" : "")
          }
        >
          {value}
        </div>
      </CardContent>
    </Card>
  );
}